import { css, cva } from '@styled-system/css';
import { Check, Folder as FolderIcon, Inbox } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';

import type { SearchFolder } from '../organization/useSearchOrganizationServer';

// Déplacement d'une recherche vers un dossier — popup simple (pattern
// MoveFilterModal d'Autocop) : liste cliquable, ✓ sur la destination choisie.

export interface MoveSearchModalProps {
  isOpen: boolean;
  searchName: string;
  folders: SearchFolder[];
  currentFolderId: string | null;
  onClose: () => void;
  onMove: (folderId: string | null) => void;
}

const list = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  paddingTop: '4',
  maxHeight: '320px',
  overflowY: 'auto',
});

const rowStyle = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    width: '100%',
    textAlign: 'left',
    paddingY: '4',
    paddingX: '5',
    borderRadius: 'md',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 'bodySm',
    fontFamily: 'sans',
    color: 'text.primary',
    transition: 'background 120ms ease, border-color 120ms ease',
    _hover: { bg: 'surface.sunken' },
  },
  variants: {
    selected: {
      true: { bg: 'brand.primary/10', borderColor: 'brand.primary/40', color: 'brand.primary' },
      false: {},
    },
  },
});

const rowIcon = css({ display: 'inline-flex', alignItems: 'center', color: 'text.tertiary', flexShrink: 0 });

const rowLabel = css({ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

const currentHint = css({ fontSize: 'micro', color: 'text.tertiary', flexShrink: 0 });

export const MoveSearchModal: FC<MoveSearchModalProps> = ({
  isOpen,
  searchName,
  folders,
  currentFolderId,
  onClose,
  onMove,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(currentFolderId);

  useEffect(() => {
    if (isOpen) setSelected(currentFolderId);
  }, [isOpen, currentFolderId]);

  const destinations: { id: string | null; name: string; icon: typeof FolderIcon }[] = [
    { id: null, name: t('folders.unfiled'), icon: Inbox },
    ...folders.map((folder) => ({ id: folder.id, name: folder.name, icon: FolderIcon })),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('folders.moveTitle')}
      subtitle={searchName}
      width="440px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={selected === currentFolderId}
            onClick={() => {
              onMove(selected);
              onClose();
            }}
          >
            {t('folders.move')}
          </Button>
        </>
      }
    >
      <div className={list}>
        {destinations.map((destination) => {
          const Icon = destination.icon;
          const isSelected = selected === destination.id;
          const isCurrent = currentFolderId === destination.id;
          return (
            <button
              key={destination.id ?? '__root__'}
              type="button"
              className={rowStyle({ selected: isSelected })}
              onClick={() => setSelected(destination.id)}
            >
              <span className={rowIcon}>
                <Icon size={14} strokeWidth={1.75} />
              </span>
              <span className={rowLabel}>{destination.name}</span>
              {isCurrent && <span className={currentHint}>{t('folders.currentFolder')}</span>}
              {isSelected && <Check size={14} strokeWidth={2} />}
            </button>
          );
        })}
      </div>
    </Modal>
  );
};
